/**
 * Utility class that allows others to wait for an event exactly once. It's basically just a callback mechanism as a
 * class, with the difference that multiple callbacks can wait for the same event
 *
 * Here is an example for listening for the creation of a model with a specific uid:
 * ```ts
 * const resolver = new OneShotResolver<Model>()
 *
 * // Listening for model creation event with uid someUID
 * resolver.subscribe((model) => { console.log(`Model with uid ${someUID} was created`) })
 *
 * // At some later point after the model was created
 * resolver.emit(myNewlyCreatedModel)
 * ```
 */
export class OneShotResolver {
    constructor() {
        this.callbacks = [];
    }
    subscribe(callback) {
        this.callbacks.push(callback);
    }
    emit(arg) {
        var _a;
        (_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.forEach(cb => { cb(arg); });
        this.callbacks.length = 0;
    }
}
/**
 * Utility class that allows others to wait for an event exactly once. The difference to OneShotResolver is that
 * it can be used to emit multiple events of the same type. The listener has to specify a specific event to listen
 * to by specifying an identifier, if an event with the same identifier is emitted then that listener will be informed
 *
 * Here is an example for listening for the creation of a model with a specific uid:
 * ```ts
 * const resolver = new MultiOneShotResolver<Model>()
 *
 * // Listening for model creation event with uid someUID
 * resolver.subscribe(someUID, (model) => { console.log(`Model with uid ${someUID} was created`) })
 *
 * // At some later point after the model was created
 * resolver.emit(someUID, myNewlyCreatedModel)
 * ```
 */
export class MultiOneShotResolver {
    constructor() {
        this.events = {};
    }
    subscribe(event, callback) {
        var _a;
        var _b;
        ((_a = (_b = this.events)[event]) !== null && _a !== void 0 ? _a : (_b[event] = [])).push(callback);
    }
    emit(event, arg) {
        var _a;
        (_a = this.events[event]) === null || _a === void 0 ? void 0 : _a.forEach(cb => { cb(arg); });
        delete this.events[event];
        const remaining = Object.keys(this.events).length;
        if (remaining > 0)
            console.debug(`[model] Resolved room, ${remaining} request remain`, this.events);
    }
}
